import { useState, useMemo, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import {
  useDeleteFolder, useUpdateFolderStatus, useRenameFolder, useRenameImage,
  useCreateFolder, useMoveFolder, useMoveImages,
} from '@/api/hooks/useMediaLibrary'
import {
  FolderOpen, Trash2, CalendarPlus, Video, Play, ChevronDown, ChevronRight,
  CheckCircle2, Clock, CircleDot, Library, Pencil, Plus, FolderPlus, GripVertical,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'
import type { MediaFolder } from '@/types'

const STATUS_CONFIG = {
  active: { label: 'Ativo', icon: CircleDot, color: 'bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400' },
  scheduled: { label: 'Agendado', icon: Clock, color: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30 dark:text-yellow-400' },
  published: { label: 'Publicado', icon: CheckCircle2, color: 'bg-green-500/15 text-green-600 border-green-500/30 dark:text-green-400' },
} as const

interface CutsPanelProps {
  folders: MediaFolder[]
  onSchedule: (folders: Array<{ id: string; name: string; clipCount: number }>) => void
}

interface ParentGroup {
  parentId: string | null
  parentName: string
  folders: MediaFolder[]
}

const isOrgFolder = (f: MediaFolder) => (f.tags || []).includes('_org')

export function CutsPanel({ folders, onSchedule }: CutsPanelProps) {
  const { t } = useLanguage()
  const deleteFolder = useDeleteFolder()
  const updateStatus = useUpdateFolderStatus()
  const renameFolder = useRenameFolder()
  const renameImage = useRenameImage()
  const createFolder = useCreateFolder()
  const moveFolder = useMoveFolder()
  const moveImages = useMoveImages()

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [playingClipId, setPlayingClipId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingClipId, setEditingClipId] = useState<string | null>(null)
  const [editingClipName, setEditingClipName] = useState('')
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [newOrgName, setNewOrgName] = useState('')
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [draggingFolderId, setDraggingFolderId] = useState<string | null>(null)
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map())
  const editInputRef = useRef<HTMLInputElement>(null)
  const clipInputRef = useRef<HTMLInputElement>(null)
  const createInputRef = useRef<HTMLInputElement>(null)

  const cutFolders = folders.filter(f => f.type === 'cuts')

  // Separate org folders from regular cut folders, build hierarchy
  const { orgFolders, ungroupedGroups, orgContents } = useMemo(() => {
    const orgs = cutFolders.filter(isOrgFolder)
    const regulars = cutFolders.filter(f => !isOrgFolder(f))
    const orgIds = new Set(orgs.map(f => f.id))

    // Map org folders to their children
    const contents = new Map<string, MediaFolder[]>()
    orgs.forEach(o => contents.set(o.id, []))

    const ungrouped: MediaFolder[] = []
    regulars.forEach(f => {
      if (f.parentId && orgIds.has(f.parentId)) {
        contents.get(f.parentId)!.push(f)
      } else {
        ungrouped.push(f)
      }
    })

    // Group ungrouped by parentId (existing behavior)
    const groupMap = new Map<string, ParentGroup>()
    ungrouped.forEach(f => {
      const key = f.parentId || '_orphan'
      if (!groupMap.has(key)) {
        groupMap.set(key, { parentId: f.parentId || null, parentName: f.parentName || t('Sem pasta'), folders: [] })
      }
      groupMap.get(key)!.folders.push(f)
    })
    const groups = Array.from(groupMap.values())
    groups.sort((a, b) => {
      if (!a.parentId) return 1
      if (!b.parentId) return -1
      return a.parentName.localeCompare(b.parentName)
    })

    return { orgFolders: orgs, ungroupedGroups: groups, orgContents: contents }
  }, [cutFolders, t])

  // ── Handlers ─────────────────────────────────────────────

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }
  const toggleGroupCollapse = (groupKey: string) => {
    setExpandedGroups(prev => { const next = new Set(prev); next.has(groupKey) ? next.delete(groupKey) : next.add(groupKey); return next })
  }
  const toggleOrgCollapse = (id: string) => {
    setExpandedOrgs(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next })
  }

  const toggleClipPlay = useCallback((clipId: string) => {
    if (playingClipId === clipId) {
      const video = videoRefs.current.get(clipId)
      if (video) { video.pause(); video.currentTime = 0 }
      setPlayingClipId(null)
    } else {
      if (playingClipId) {
        const prev = videoRefs.current.get(playingClipId)
        if (prev) { prev.pause(); prev.currentTime = 0 }
      }
      setPlayingClipId(clipId)
      const video = videoRefs.current.get(clipId)
      if (video) video.play()
    }
  }, [playingClipId])

  const startEditing = (folder: MediaFolder, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingId(folder.id)
    setEditingName(folder.name)
    setTimeout(() => editInputRef.current?.select(), 0)
  }

  const saveRename = () => {
    if (!editingId) return
    const trimmed = editingName.trim()
    if (trimmed && trimmed !== folders.find(f => f.id === editingId)?.name) {
      renameFolder.mutate({ folderId: editingId, name: trimmed }, {
        onSuccess: () => toast.success(t('Nome atualizado')),
      })
    }
    setEditingId(null)
  }

  const startEditingClip = (clipId: string, filename: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setEditingClipId(clipId)
    setEditingClipName(filename)
    setTimeout(() => clipInputRef.current?.select(), 0)
  }

  const saveClipRename = (folderId: string) => {
    if (!editingClipId) return
    const trimmed = editingClipName.trim()
    if (trimmed) {
      renameImage.mutate({ folderId, imageId: editingClipId, filename: trimmed }, {
        onSuccess: () => toast.success(t('Nome atualizado')),
      })
    }
    setEditingClipId(null)
  }

  const selectAllInGroup = (groupFolders: MediaFolder[]) => {
    const ids = groupFolders.map(f => f.id)
    const allSelected = ids.every(id => selectedIds.has(id))
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allSelected) ids.forEach(id => next.delete(id))
      else ids.forEach(id => next.add(id))
      return next
    })
  }

  const handleDelete = (folderId: string, name: string) => {
    if (!confirm(`${t('Deletar pasta de cortes')} "${name}"?`)) return
    deleteFolder.mutate(folderId, { onSuccess: () => toast.success(t('Pasta deletada')) })
  }

  const handleDeleteGroup = (groupFolders: MediaFolder[], label: string) => {
    const msg = groupFolders.length === 1
      ? `${t('Deletar pasta de cortes')} "${groupFolders[0].name}"?`
      : `${t('Deletar')} ${groupFolders.length} ${t('pastas de cortes de')} "${label}"?`
    if (!confirm(msg)) return
    groupFolders.forEach(f => deleteFolder.mutate(f.id))
    toast.success(t('Pastas deletadas'))
  }

  const handleDeleteOrg = (org: MediaFolder) => {
    const children = orgContents.get(org.id) || []
    const msg = children.length > 0
      ? `${t('Deletar pasta')} "${org.name}" ${t('e mover')} ${children.length} ${t('pasta(s) de cortes para fora')}?`
      : `${t('Deletar pasta')} "${org.name}"?`
    if (!confirm(msg)) return
    // Move children to root first
    children.forEach(child => {
      moveFolder.mutate({ folderId: child.id, parentId: null })
    })
    deleteFolder.mutate(org.id, { onSuccess: () => toast.success(t('Pasta deletada')) })
  }

  const cycleStatus = (folder: MediaFolder) => {
    const next = folder.status === 'active' ? 'scheduled' : folder.status === 'scheduled' ? 'published' : 'active'
    updateStatus.mutate({ folderId: folder.id, status: next }, {
      onSuccess: () => toast.success(`Status: ${STATUS_CONFIG[next].label}`),
    })
  }

  const handleScheduleSelected = () => {
    const selected = cutFolders
      .filter(f => selectedIds.has(f.id))
      .map(f => ({ id: f.id, name: f.name, clipCount: f.images.length }))
    if (selected.length === 0) return
    onSchedule(selected)
    setSelectedIds(new Set())
  }

  const handleScheduleGroup = (groupFolders: MediaFolder[]) => {
    const items = groupFolders.map(f => ({ id: f.id, name: f.name, clipCount: f.images.length }))
    if (items.length === 0) return
    onSchedule(items)
  }

  // ── Create org folder ────────────────────────────────────

  const handleCreateOrg = () => {
    const trimmed = newOrgName.trim()
    if (!trimmed) return
    createFolder.mutate({ name: trimmed, type: 'cuts', tags: ['_org'] }, {
      onSuccess: () => {
        toast.success(t('Pasta criada'))
        setNewOrgName('')
        setShowCreateOrg(false)
      },
      onError: () => toast.error(t('Erro ao criar pasta')),
    })
  }

  // ── Drag and drop ────────────────────────────────────────

  const handleFolderDragStart = (e: React.DragEvent, folderId: string) => {
    e.dataTransfer.setData('application/x-media-folder', folderId)
    e.dataTransfer.effectAllowed = 'move'
    setDraggingFolderId(folderId)
  }

  const handleDragEnd = () => {
    setDraggingFolderId(null)
    setDragOverId(null)
  }

  const handleDragOver = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragOverId !== targetId) setDragOverId(targetId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    // Only clear if leaving the actual element, not entering a child
    const related = e.relatedTarget as Node | null
    if (!related || !(e.currentTarget as HTMLElement).contains(related)) {
      setDragOverId(null)
    }
  }

  const handleDropOnOrg = (e: React.DragEvent, orgId: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverId(null)

    const folderData = e.dataTransfer.getData('application/x-media-folder')
    if (folderData && folderData !== orgId) {
      // Don't allow dropping an org folder into another org folder
      if (cutFolders.find(f => f.id === folderData && isOrgFolder(f))) {
        toast.error(t('Pastas organizacionais nao podem ser aninhadas'))
        return
      }
      moveFolder.mutate({ folderId: folderData, parentId: orgId }, {
        onSuccess: () => {
          toast.success(t('Pasta movida'))
          setExpandedOrgs(prev => new Set(prev).add(orgId))
        },
        onError: () => toast.error(t('Erro ao mover pasta')),
      })
      return
    }

    const imageData = e.dataTransfer.getData('application/x-media-images')
    if (imageData) {
      toast.error(t('Arraste clips para uma pasta de cortes'))
    }
  }

  const handleDropOnCutFolder = (e: React.DragEvent, targetFolderId: string, orgParentId?: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverId(null)

    const imageData = e.dataTransfer.getData('application/x-media-images')
    if (imageData) {
      try {
        const imageIds: string[] = JSON.parse(imageData)
        moveImages.mutate({ imageIds, targetFolderId }, {
          onSuccess: () => toast.success(t('Clip(s) movido(s)')),
          onError: () => toast.error(t('Erro ao mover clips')),
        })
      } catch { /* ignore */ }
      return
    }

    const folderData = e.dataTransfer.getData('application/x-media-folder')
    if (folderData && folderData !== targetFolderId) {
      // If this cut folder is inside an org, move the dragged folder into the same org
      if (orgParentId) {
        if (cutFolders.find(f => f.id === folderData && isOrgFolder(f))) {
          toast.error(t('Pastas organizacionais nao podem ser aninhadas'))
          return
        }
        moveFolder.mutate({ folderId: folderData, parentId: orgParentId }, {
          onSuccess: () => {
            toast.success(t('Pasta movida'))
            setExpandedOrgs(prev => new Set(prev).add(orgParentId))
          },
          onError: () => toast.error(t('Erro ao mover pasta')),
        })
      } else {
        toast.error(t('Arraste pastas para uma pasta organizacional'))
      }
    }
  }

  const handleDropOnRoot = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOverId(null)
    const folderData = e.dataTransfer.getData('application/x-media-folder')
    if (folderData) {
      const folder = cutFolders.find(f => f.id === folderData)
      if (folder && folder.parentId) {
        const parentIsOrg = cutFolders.find(f => f.id === folder.parentId && isOrgFolder(f))
        if (parentIsOrg) {
          moveFolder.mutate({ folderId: folderData, parentId: null }, {
            onSuccess: () => toast.success(t('Pasta removida da organizacao')),
          })
        }
      }
    }
  }

  const handleClipDragStart = (e: React.DragEvent, clipId: string) => {
    e.dataTransfer.setData('application/x-media-images', JSON.stringify([clipId]))
    e.dataTransfer.effectAllowed = 'move'
  }

  // ── Render helpers ───────────────────────────────────────

  const renderClipGrid = (folder: MediaFolder) => {
    if (folder.images.length === 0) return null
    return (
      <div className="border-t border-border bg-muted/20 p-3 pl-8">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
          {folder.images.map(clip => {
            const isPlaying = playingClipId === clip.id
            return (
              <div
                key={clip.id}
                className="group relative overflow-hidden rounded-md border border-border"
                draggable
                onDragStart={e => handleClipDragStart(e, clip.id)}
              >
                <div className="relative aspect-video cursor-pointer" onClick={() => toggleClipPlay(clip.id)}>
                  <video
                    ref={el => { if (el) videoRefs.current.set(clip.id, el); else videoRefs.current.delete(clip.id) }}
                    src={clip.url}
                    className="h-full w-full object-cover"
                    preload="metadata" playsInline
                    onEnded={() => setPlayingClipId(null)}
                  />
                  {!isPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                      <Play className="h-5 w-5 fill-white text-white opacity-80" />
                    </div>
                  )}
                </div>
                {clip.clip && (
                  <div className="absolute top-1 left-1">
                    <Badge className="border-none bg-black/70 text-[8px] text-white px-1 py-0">
                      {clip.clip.part}/{clip.clip.total}
                    </Badge>
                  </div>
                )}
                <div className="px-1.5 py-1 bg-muted/60">
                  {editingClipId === clip.id ? (
                    <input
                      ref={clipInputRef}
                      value={editingClipName}
                      onChange={e => setEditingClipName(e.target.value)}
                      onBlur={() => saveClipRename(folder.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveClipRename(folder.id)
                        if (e.key === 'Escape') setEditingClipId(null)
                      }}
                      onClick={e => e.stopPropagation()}
                      className="w-full text-[9px] bg-background border border-primary/40 rounded px-1 py-0 outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  ) : (
                    <p
                      className="truncate text-[9px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
                      onClick={e => startEditingClip(clip.id, clip.filename, e)}
                      title={t('Clique para renomear')}
                    >
                      {clip.filename}
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderFolderRow = (folder: MediaFolder, indent: boolean, orgParentId?: string) => {
    const isExpanded = expandedId === folder.id
    const isSelected = selectedIds.has(folder.id)
    const clipCount = folder.images.length
    const statusCfg = STATUS_CONFIG[folder.status] || STATUS_CONFIG.active
    const StatusIcon = statusCfg.icon
    const isDragOver = dragOverId === folder.id
    const isDragging = draggingFolderId === folder.id

    return (
      <div key={folder.id} className={cn(isDragging && 'opacity-40')}>
        <div
          className={cn(
            'flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors',
            indent && 'pl-8',
            isSelected ? 'bg-primary/5' : 'hover:bg-muted/30',
            isDragOver && 'ring-1 ring-inset ring-primary bg-primary/10',
          )}
          draggable={!editingId}
          onDragStart={e => handleFolderDragStart(e, folder.id)}
          onDragEnd={handleDragEnd}
          onDragOver={e => handleDragOver(e, folder.id)}
          onDragLeave={handleDragLeave}
          onDrop={e => handleDropOnCutFolder(e, folder.id, orgParentId)}
          onClick={() => setExpandedId(isExpanded ? null : folder.id)}
        >
          <GripVertical className="h-3 w-3 text-muted-foreground/40 shrink-0 cursor-grab" />

          {/* Checkbox */}
          <button
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
              isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40',
            )}
            onClick={(e) => { e.stopPropagation(); toggleSelect(folder.id) }}
          >
            {isSelected && <CheckCircle2 className="h-2.5 w-2.5 text-primary-foreground" />}
          </button>

          {/* Expand */}
          {isExpanded
            ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
            : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
          }

          {/* Info */}
          <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            {editingId === folder.id ? (
              <input
                ref={editInputRef}
                value={editingName}
                onChange={e => setEditingName(e.target.value)}
                onBlur={saveRename}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveRename()
                  if (e.key === 'Escape') setEditingId(null)
                }}
                onClick={e => e.stopPropagation()}
                className="w-full text-xs font-medium bg-background border border-primary/40 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-primary/30"
              />
            ) : (
              <div className="flex items-center gap-1">
                <p className="truncate text-xs font-medium">{folder.name}</p>
                <button onClick={e => startEditing(folder, e)} className="shrink-0" title={t('Renomear')}>
                  <Pencil className="h-2.5 w-2.5 text-muted-foreground/50 hover:text-foreground" />
                </button>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">{clipCount} clip{clipCount !== 1 ? 's' : ''}</p>
          </div>

          {/* Status */}
          <button
            onClick={(e) => { e.stopPropagation(); cycleStatus(folder) }}
            className={cn('flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-medium transition-colors', statusCfg.color)}
            title={t('Clique para mudar status')}
          >
            <StatusIcon className="h-2.5 w-2.5" />
            {statusCfg.label}
          </button>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
              onClick={(e) => { e.stopPropagation(); onSchedule([{ id: folder.id, name: folder.name, clipCount }]) }}
              title={t('Agendar')}
            >
              <CalendarPlus className="h-3.5 w-3.5" />
            </button>
            <button
              className="rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
              onClick={(e) => { e.stopPropagation(); handleDelete(folder.id, folder.name) }}
              title={t('Deletar')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Expanded: clip grid */}
        {isExpanded && clipCount > 0 && renderClipGrid(folder)}
      </div>
    )
  }

  // ── Empty state ──────────────────────────────────────────

  if (cutFolders.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-center px-4">
          <Video className="h-16 w-16 text-muted-foreground/15" />
          <p className="text-sm text-muted-foreground">{t('Nenhum corte realizado')}</p>
          <p className="text-xs text-muted-foreground/60">{t('Use a aba "Cortar Videos" para criar cortes')}</p>
        </div>
      </div>
    )
  }

  // ── Main render ──────────────────────────────────────────

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {cutFolders.filter(f => !isOrgFolder(f)).length} {t('pastas de cortes')}
          </span>
          <button
            className="flex items-center gap-1 rounded-md border border-dashed border-primary/40 px-2 py-1 text-[10px] font-medium text-primary hover:bg-primary/5 transition-colors"
            onClick={() => { setShowCreateOrg(true); setTimeout(() => createInputRef.current?.focus(), 0) }}
            title={t('Nova pasta organizacional')}
          >
            <Plus className="h-3 w-3" />
            {t('Pasta')}
          </button>
        </div>
        {selectedIds.size > 0 && (
          <Button size="sm" className="h-7 gap-1 text-xs" onClick={handleScheduleSelected}>
            <CalendarPlus className="h-3 w-3" /> {t('Agendar')} {selectedIds.size}
          </Button>
        )}
      </div>

      {/* Create org folder form */}
      {showCreateOrg && (
        <div className="flex items-center gap-2 border-b border-border px-4 py-2 bg-muted/30">
          <FolderPlus className="h-4 w-4 text-primary shrink-0" />
          <input
            ref={createInputRef}
            value={newOrgName}
            onChange={e => setNewOrgName(e.target.value)}
            placeholder={t('Nome da pasta...')}
            className="flex-1 text-xs bg-background border border-border rounded px-2 py-1 outline-none focus:ring-1 focus:ring-primary/30"
            onKeyDown={e => {
              if (e.key === 'Enter') handleCreateOrg()
              if (e.key === 'Escape') { setShowCreateOrg(false); setNewOrgName('') }
            }}
          />
          <button
            onClick={handleCreateOrg}
            disabled={createFolder.isPending || !newOrgName.trim()}
            className="rounded p-1 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => { setShowCreateOrg(false); setNewOrgName('') }}
            className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto p-3 space-y-3"
        onDragOver={e => { e.preventDefault(); setDragOverId('_root') }}
        onDragLeave={e => {
          const related = e.relatedTarget as Node | null
          if (!related || !(e.currentTarget as HTMLElement).contains(related)) setDragOverId(null)
        }}
        onDrop={handleDropOnRoot}
      >
        {/* Organizational folders */}
        {orgFolders.map(org => {
          const isOrgExpanded = expandedOrgs.has(org.id)
          const children = orgContents.get(org.id) || []
          const totalClips = children.reduce((sum, f) => sum + f.images.length, 0)
          const childIds = children.map(f => f.id)
          const isDragOver = dragOverId === org.id || (draggingFolderId && childIds.includes(dragOverId || ''))
          const allSelected = children.length > 0 && children.every(f => selectedIds.has(f.id))

          return (
            <div
              key={org.id}
              className={cn(
                'rounded-xl border overflow-hidden transition-all',
                isDragOver ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border',
              )}
              onDragOver={e => handleDragOver(e, org.id)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDropOnOrg(e, org.id)}
            >
              {/* Org header */}
              <div
                className="flex items-center gap-2 px-3 py-2.5 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors"
                onClick={() => toggleOrgCollapse(org.id)}
              >
                {isOrgExpanded
                  ? <ChevronDown className="h-4 w-4 text-primary shrink-0" />
                  : <ChevronRight className="h-4 w-4 text-primary shrink-0" />
                }
                <FolderPlus className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  {editingId === org.id ? (
                    <input
                      ref={editInputRef}
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onBlur={saveRename}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveRename()
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                      onClick={e => e.stopPropagation()}
                      className="w-full text-xs font-semibold bg-background border border-primary/40 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  ) : (
                    <div className="flex items-center gap-1">
                      <p className="truncate text-xs font-semibold text-primary">{org.name}</p>
                      <button onClick={e => startEditing(org, e)} className="shrink-0" title={t('Renomear')}>
                        <Pencil className="h-2.5 w-2.5 text-primary/50 hover:text-primary" />
                      </button>
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    {children.length} {children.length !== 1 ? t('pastas') : t('pasta')} &middot; {totalClips} clips
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                  {children.length > 0 && (
                    <>
                      <button
                        className={cn(
                          'rounded px-2 py-0.5 text-[10px] font-medium border transition-colors',
                          allSelected
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/30',
                        )}
                        onClick={() => selectAllInGroup(children)}
                      >
                        {allSelected ? t('Desmarcar') : t('Selecionar')}
                      </button>
                      <button
                        className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => handleScheduleGroup(children)}
                        title={t('Agendar tudo')}
                      >
                        <CalendarPlus className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  <button
                    className="rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
                    onClick={() => handleDeleteOrg(org)}
                    title={t('Deletar pasta')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Org children */}
              {isOrgExpanded && (
                <div className="divide-y divide-border">
                  {children.length === 0 ? (
                    <div className="px-8 py-4 text-center text-[10px] text-muted-foreground/60">
                      {t('Arraste pastas de cortes para ca')}
                    </div>
                  ) : (
                    children.map(folder => renderFolderRow(folder, true, org.id))
                  )}
                </div>
              )}
            </div>
          )
        })}

        {/* Ungrouped cut folders (original behavior) */}
        {ungroupedGroups.map(group => {
          const groupKey = group.parentId || '_orphan'
          const isGroupCollapsed = !expandedGroups.has(groupKey)
          const totalClips = group.folders.reduce((sum, f) => sum + f.images.length, 0)
          const allGroupSelected = group.folders.every(f => selectedIds.has(f.id))

          return (
            <div key={groupKey} className="rounded-xl border border-border overflow-hidden">
              {/* Group header */}
              <div
                className="flex items-center gap-2 px-3 py-2 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
                onClick={() => toggleGroupCollapse(groupKey)}
              >
                {isGroupCollapsed
                  ? <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                }
                <Library className="h-4 w-4 shrink-0 text-primary/70" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold">{group.parentName}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {group.folders.length} {group.folders.length !== 1 ? t('videos processados') : t('video processado')} &middot; {totalClips} clips
                  </p>
                </div>

                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                  <button
                    className={cn(
                      'rounded px-2 py-0.5 text-[10px] font-medium border transition-colors',
                      allGroupSelected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/30',
                    )}
                    onClick={() => selectAllInGroup(group.folders)}
                  >
                    {allGroupSelected ? t('Desmarcar') : t('Selecionar')}
                  </button>
                  <button
                    className="rounded p-1 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => handleScheduleGroup(group.folders)}
                    title={t('Agendar tudo')}
                  >
                    <CalendarPlus className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="rounded p-1 text-muted-foreground hover:text-destructive transition-colors"
                    onClick={() => handleDeleteGroup(group.folders, group.parentName)}
                    title={t('Deletar tudo')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Sub-folders */}
              {!isGroupCollapsed && (
                <div className="divide-y divide-border">
                  {group.folders.map(folder => renderFolderRow(folder, true))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
